import {
  SITE_PASSWORD_LOCKED_ATTR,
  SITE_PASSWORD_UNLOCK_CHANGE_EVENT,
  SITE_PASSWORD_UNLOCK_KEY,
  SITE_PASSWORD_UNLOCK_VALUE,
} from '@/constants/sitePassword';
import {
  getSitePassword,
  getSitePasswordEmbargoEndsAtMs,
  isSitePasswordEnabled,
} from '@/lib/env';

/**
 * Stable snapshot cache for `useSyncExternalStore`.
 * `getSnapshot` must return the same reference when the stored value is unchanged.
 */
let cachedRaw: string | null | undefined;
let cachedUnlocked = false;

function setUnlockCache(raw: string | null, unlocked: boolean): void {
  cachedRaw = raw;
  cachedUnlocked = unlocked;
}

/**
 * Whether the soft gate should run for this build + clock.
 * Requires ENABLED=true, a non-empty password, and a valid embargo end in the future.
 */
export function isSiteGateActive(now = Date.now()): boolean {
  if (!isSitePasswordEnabled()) return false;
  if (getSitePassword() == null) return false;

  const endsAt = getSitePasswordEmbargoEndsAtMs();
  if (endsAt == null) return false;

  return now < endsAt;
}

export function passwordMatches(input: string): boolean {
  const expected = getSitePassword();
  if (expected == null) return false;
  return input.trim() === expected;
}

/**
 * Re-read localStorage into the snapshot cache.
 * Call from `storage` event handlers so other tabs can update unlock state.
 */
export function syncSitePasswordUnlockCacheFromStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    const stored = window.localStorage.getItem(SITE_PASSWORD_UNLOCK_KEY);
    if (stored === cachedRaw) return;
    setUnlockCache(stored, stored === SITE_PASSWORD_UNLOCK_VALUE);
  } catch {
    // Keep existing cache if storage is unavailable.
  }
}

export function readSitePasswordUnlocked(): boolean {
  if (typeof window === 'undefined') return false;

  if (cachedRaw !== undefined) {
    return cachedUnlocked;
  }

  try {
    const stored = window.localStorage.getItem(SITE_PASSWORD_UNLOCK_KEY);
    const unlocked = stored === SITE_PASSWORD_UNLOCK_VALUE;
    setUnlockCache(stored, unlocked);
    return unlocked;
  } catch {
    setUnlockCache(null, false);
    return false;
  }
}

export function writeSitePasswordUnlocked(): void {
  if (typeof window === 'undefined') return;

  setUnlockCache(SITE_PASSWORD_UNLOCK_VALUE, true);

  try {
    window.localStorage.setItem(SITE_PASSWORD_UNLOCK_KEY, SITE_PASSWORD_UNLOCK_VALUE);
  } catch {
    // Private mode / quota — still notify listeners so UI can update.
  }

  window.dispatchEvent(new CustomEvent(SITE_PASSWORD_UNLOCK_CHANGE_EVENT));
}

/** Build-time values for the pre-paint lock script (JSON-serializable). */
export function getSitePasswordBootConfig(): {
  enabled: boolean;
  hasPassword: boolean;
  embargoEndsAtMs: number | null;
  unlockKey: string;
  unlockValue: string;
} {
  return {
    enabled: isSitePasswordEnabled(),
    hasPassword: getSitePassword() != null,
    embargoEndsAtMs: getSitePasswordEmbargoEndsAtMs(),
    unlockKey: SITE_PASSWORD_UNLOCK_KEY,
    unlockValue: SITE_PASSWORD_UNLOCK_VALUE,
  };
}

/**
 * Inline script source that sets `data-site-locked` before paint when the gate
 * would be active and localStorage has no unlock flag.
 */
export function getSitePasswordBootScript(): string {
  const config = getSitePasswordBootConfig();
  if (!config.enabled || !config.hasPassword || config.embargoEndsAtMs == null) {
    return '';
  }

  // Config is build-time JSON only — safe to embed.
  return `(function(){try{var c=${JSON.stringify({
    embargoEndsAtMs: config.embargoEndsAtMs,
    unlockKey: config.unlockKey,
    unlockValue: config.unlockValue,
    lockedAttr: SITE_PASSWORD_LOCKED_ATTR,
  })};if(Date.now()>=c.embargoEndsAtMs)return;var u=null;try{u=localStorage.getItem(c.unlockKey);}catch(e){}if(u===c.unlockValue)return;document.documentElement.setAttribute(c.lockedAttr,'');}catch(e){}})();`;
}

/** Clears the pre-paint lock attribute after unlock or when the gate is inactive. */
export function clearSitePasswordLockedAttr(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.removeAttribute(SITE_PASSWORD_LOCKED_ATTR);
}
